import { useState } from "react";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  Autocomplete,
  TextField,
  Tooltip,
  Typography,
  Box,
  FormLabel,
  Skeleton,
  IconButton,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import RefreshIcon from "@mui/icons-material/Refresh";
import ListboxComponent from "./list-box-virtualized";

const normalizeCnpjForSearch = (str) =>
  (str || "").replace(/[./-]/g, "");

function createFilterOptionsWithCnpjNormalization(getLabel) {
  const defaultFilter = createFilterOptions();
  return (options, state) => {
    const inputValue = (state?.inputValue || "").trim();
    const defaultFiltered = defaultFilter(options, state);
    const normalizedInput = normalizeCnpjForSearch(inputValue);
    if (normalizedInput.length < 2) return defaultFiltered;
    const defaultSet = new Set(defaultFiltered);
    const extra = options.filter((option) => {
      if (defaultSet.has(option)) return false;
      const label = getLabel(option);
      const parts = `${label}`.split(/\s*\|\s*/);
      const rightPart = parts.length >= 2 ? parts[parts.length - 1] : "";
      const normalizedRight = normalizeCnpjForSearch(rightPart);
      return normalizedRight.includes(normalizedInput);
    });
    return extra.length ? [...defaultFiltered, ...extra] : defaultFiltered;
  };
}

export default function AutoCompleteField({
  optionLabelKey,
  optionIdKey,
  options,
  label,
  value,
  onChange,
  onInputChange,
  disabled,
  loading = false,
  optionsSearchLoading = false,
  multiple = false,
  required = false,
  customTooltipText,
  onRefresh,
  showRefreshButton = false,
  ignoreCnpjPunctuationInSearch = false,
  /** Quando true e o campo não está `disabled`, força o painel do Autocomplete fechado (ex.: erro na API com lista vazia e input vazio — o usuário ainda pode digitar). */
  blockDropdownWhen = false,
  /** Se true, durante `loading` o Autocomplete permanece montado (evita perder o texto ao buscar opções na API). Mostra um indicador no campo. */
  preserveInputWhenLoading = false,
  /** Lista já filtrada no servidor — não aplica filtro local do MUI (evita sumir opções válidas). */
  disableClientFilter = false,
}) {
  const serverSearchControlledInput =
    preserveInputWhenLoading && typeof onInputChange === "function";
  const [controlledInputValue, setControlledInputValue] = useState("");

  const getLabel = (option) => option?.[optionLabelKey] || option?.Nome || "";

  const getId = (item) => {
    if (item == null || item === false || item === undefined) return null;
    return item?.[optionIdKey] ?? item?.Id ?? item?.id ?? item;
  };

  const filterOptions = disableClientFilter
    ? (options) => options
    : ignoreCnpjPunctuationInSearch
      ? createFilterOptionsWithCnpjNormalization(getLabel)
      : undefined;

  /** MUI Autocomplete (single) exige `null` sem seleção — `""` dispara aviso e pode quebrar o fluxo. */
  const autocompleteValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value === "" || value === null || value === undefined
      ? null
      : value;

  const mergedOnInputChange = serverSearchControlledInput
    ? (event, newValue, reason) => {
        if (reason === "input") {
          setControlledInputValue(newValue ?? "");
        }
        onInputChange(event, newValue, reason);
      }
    : onInputChange;

  const listLoading =
    preserveInputWhenLoading &&
    Boolean(loading || optionsSearchLoading);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        {label && (
          <FormLabel
            sx={{
              color: themeColorsDesingSystem.text.primary,
              fontSize: ".875rem",
              fontWeight: "600",
              lineHeight: 1.5,
              mb: 0,
              fontFamily: "Open Sans,sans-serif",
              flex: 1,
            }}
          >
            {label}
            {required ? " *" : ""}
          </FormLabel>
        )}
        {showRefreshButton && onRefresh && (
          <Tooltip title="Recarregar opções">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (onRefresh) onRefresh();
              }}
              disabled={disabled || loading}
              sx={{
                padding: "4px",
                color: themeColorsDesingSystem.primary.main,
                "&:hover": {
                  backgroundColor: themeColorsDesingSystem.primary.light + "20",
                },
                "&.Mui-disabled": {
                  color: themeColorsDesingSystem.text.disabled,
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {loading && !preserveInputWhenLoading ? (
        <Skeleton height="2.3em" sx={{ transform: "none" }} />
      ) : (
        <Autocomplete
          blurOnSelect
          disabled={disabled}
          {...(blockDropdownWhen && !disabled ? { open: false } : {})}
          multiple={multiple}
          options={options}
          value={autocompleteValue}
          onChange={(_, newValue) => {
            if (onChange) onChange(newValue);
          }}
          loading={listLoading}
          {...(serverSearchControlledInput
            ? { inputValue: controlledInputValue }
            : {})}
          onInputChange={mergedOnInputChange}
          fullWidth
          filterOptions={filterOptions}
          isOptionEqualToValue={(option, val) => getId(option) === getId(val)}
          getOptionLabel={(option) => getLabel(option)}
          noOptionsText="Nenhuma opção encontrada"
          ListboxComponent={ListboxComponent}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: themeColorsDesingSystem.primary.main,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: themeColorsDesingSystem.stone.light,
                    cursor: "default",
                  },
                },
                "& .MuiInputBase-input": {
                  fontFamily: "Open Sans,sans-serif",
                  color: themeColorsDesingSystem.text.primary,
                  fontSize: ".875rem",
                  padding: "0 4px 0 5px!important",
                },
                "& .MuiIconButton-root": {
                  padding: 0,
                },
                "& .MuiAutocomplete-root": {
                  marginBottom: "0px",
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Tooltip
                arrow
                title={
                  customTooltipText
                    ? customTooltipText(option)
                    : getLabel(option)
                }
                leaveTouchDelay={100}
                disableInteractive
              >
                <Typography
                  noWrap
                  sx={{
                    fontFamily: "Open Sans,sans-serif",
                    color: themeColorsDesingSystem.text.primary,
                    fontSize: ".875rem",
                  }}
                >
                  {getLabel(option)}
                </Typography>
              </Tooltip>
            </li>
          )}
        />
      )}
    </Box>
  );
}
